import React, { useMemo, useState } from 'react';
import { Plus, ChevronDown, ChevronRight, MessageSquare, Paperclip, AlertTriangle, Clock } from 'lucide-react';
import { STATUS_BY_ID, PRIORITY_BY_ID } from './constants';

// Compact pill that shows status next to a task title in the list.
function StatusPill({ status }) {
  const s = STATUS_BY_ID[status] || STATUS_BY_ID.backlog;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 10,
      fontSize: 10, fontWeight: 700, letterSpacing: 0.3,
      color: s.color, background: s.bg, whiteSpace: 'nowrap',
    }}>{s.label}</span>
  );
}

function PriorityFlag({ priority }) {
  const p = PRIORITY_BY_ID[priority] || PRIORITY_BY_ID.medium;
  return (
    <span title={p.label} style={{ color: p.color, fontWeight: 700, fontSize: 13, lineHeight: 1 }}>
      {p.flag}
    </span>
  );
}

function dueLabel(dueDate) {
  if (!dueDate) return null;
  const due = new Date(dueDate);
  const now = new Date();
  const today0 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((due - today0) / 86400000);
  let txt;
  if (days < 0) txt = `${Math.abs(days)}d overdue`;
  else if (days === 0) txt = 'Due today';
  else if (days === 1) txt = 'Due tomorrow';
  else if (days < 7) txt = `Due in ${days}d`;
  else txt = due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return { txt, overdue: days < 0, soon: days >= 0 && days <= 2 };
}

function QuickAddRow({ onAdd, areaName }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await onAdd(title.trim());
      setTitle('');
      setOpen(false);
    } finally { setSaving(false); }
  };
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px',
          background: 'transparent', border: '1px dashed var(--gray-300)', borderRadius: 6,
          fontSize: 12, color: 'var(--gray-500)', cursor: 'pointer', width: '100%',
        }}>
        <Plus size={13} /> Add task to {areaName || 'area'}
      </button>
    );
  }
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '4px 0' }}>
      <input
        autoFocus
        value={title}
        onChange={e => setTitle(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') submit();
          if (e.key === 'Escape') { setTitle(''); setOpen(false); }
        }}
        placeholder="Task title (Enter to save, Esc to cancel)"
        style={{
          flex: 1, padding: '7px 10px', border: '1px solid var(--gray-300)',
          borderRadius: 6, fontSize: 12.5, outline: 'none',
        }}
      />
      <button
        onClick={submit}
        disabled={saving || !title.trim()}
        style={{
          padding: '7px 12px', background: saving ? 'var(--gray-300)' : 'var(--orange)',
          color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600,
          cursor: saving ? 'wait' : 'pointer',
        }}>Add</button>
    </div>
  );
}

export default function TaskList({
  view,            // 'epic-N' | 'backlog' | 'mine' | 'all'
  tasks = [],
  areas = [],
  selectedId,
  onSelect,
  onQuickAdd,
  filterArea,
  filterEpic,
  filterStatus,
  filterPriority,
  filterAssignee,
  search,
}) {
  const [collapsed, setCollapsed] = useState(() => new Set());

  const grouped = useMemo(() => {
    // Group by area (preserves area sort_order from `areas`).
    const filtered = tasks.filter(t => {
      if (filterArea && String(t.area_id) !== String(filterArea)) return false;
      if (filterEpic !== undefined && filterEpic !== '') {
        if (String(filterEpic) === '0') { if (t.epic_id) return false; }
        else if (String(t.epic_id || '') !== String(filterEpic)) return false;
      }
      if (filterStatus && t.status !== filterStatus) return false;
      if (filterPriority && t.priority !== filterPriority) return false;
      if (filterAssignee && String(t.assignee_id || '') !== String(filterAssignee)) return false;
      if (search && search.trim()) {
        const q = search.trim().toLowerCase();
        const hay = `${t.title || ''} ${t.area_name || ''} ${t.assignee_name || ''} ${t.epic_name || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const byArea = new Map();
    for (const t of filtered) {
      const key = t.area_id || 0;
      if (!byArea.has(key)) byArea.set(key, []);
      byArea.get(key).push(t);
    }
    // Build ordered list of [areaRow, tasks]
    const orderedAreas = areas.slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    const out = [];
    for (const a of orderedAreas) {
      const list = byArea.get(a.id) || [];
      // For Epic / Mine views, hide empty areas (cleaner roadmap feel).
      // For Backlog / All, show empty so the user sees the bucket exists.
      const isActiveView = view === 'mine' || /^epic-\d+$/.test(view || '');
      if (list.length === 0 && isActiveView) continue;
      out.push({ area: a, tasks: list });
    }
    // No-area bucket for orphan tasks
    const orphan = byArea.get(0) || [];
    if (orphan.length) out.push({ area: { id: 0, name: '(No area)', color: null, slug: 'no-area' }, tasks: orphan });
    return out;
  }, [tasks, areas, filterArea, filterStatus, filterPriority, filterAssignee, search, view]);

  const toggle = (areaId) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(areaId)) next.delete(areaId); else next.add(areaId);
      return next;
    });
  };

  if (!grouped.length) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>
        No tasks match this view.
      </div>
    );
  }

  return (
    <div style={{ padding: '8px 0' }}>
      {grouped.map(({ area, tasks }) => {
        const isCollapsed = collapsed.has(area.id);
        const total = tasks.length;
        return (
          <div key={area.id} style={{ marginBottom: 18 }}>
            <div
              onClick={() => toggle(area.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '6px 12px', cursor: 'pointer', userSelect: 'none',
                borderBottom: '1px solid var(--gray-200)',
                background: '#fff', position: 'sticky', top: 0, zIndex: 1,
              }}>
              {isCollapsed ? <ChevronRight size={14} color="var(--gray-500)" /> : <ChevronDown size={14} color="var(--gray-500)" />}
              <span style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 13 }}>{area.name}</span>
              <span style={{
                marginLeft: 6, padding: '1px 7px', borderRadius: 8,
                background: 'var(--gray-100)', color: 'var(--gray-600)', fontSize: 10, fontWeight: 600,
              }}>{total}</span>
            </div>
            {!isCollapsed && (
              <div style={{ padding: '4px 12px 0 12px' }}>
                {tasks.map(t => {
                  const due = dueLabel(t.due_date);
                  const isSel = selectedId === t.id;
                  const subSummary = t.subtask_count
                    ? `${t.subtask_done_count}/${t.subtask_count} subtasks`
                    : null;
                  return (
                    <div
                      key={t.id}
                      onClick={() => onSelect(t.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '8px 10px', cursor: 'pointer',
                        borderRadius: 6, marginBottom: 4,
                        background: isSel ? '#fff7ed' : 'transparent',
                        borderLeft: isSel ? '3px solid var(--orange)' : '3px solid transparent',
                      }}
                      onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = 'var(--gray-50, #fafbfc)'; }}
                      onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = 'transparent'; }}>
                      <PriorityFlag priority={t.priority} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                          <span style={{
                            fontSize: 13, fontWeight: 600, color: 'var(--gray-800)',
                            textDecoration: t.status === 'done' ? 'line-through' : 'none',
                            opacity: t.status === 'done' ? 0.6 : 1,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>{t.title}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--gray-500)' }}>
                          <StatusPill status={t.status} />
                          {t.assignee_name && (
                            <span style={{ color: 'var(--gray-600)' }}>👤 {t.assignee_name}</span>
                          )}
                          {due && (
                            <span style={{
                              color: due.overdue ? '#dc2626' : (due.soon ? '#EA733D' : 'var(--gray-500)'),
                              fontWeight: due.overdue ? 600 : 400,
                              display: 'inline-flex', alignItems: 'center', gap: 3,
                            }}>
                              {due.overdue ? <AlertTriangle size={11} /> : <Clock size={11} />}
                              {due.txt}
                            </span>
                          )}
                          {subSummary && <span>📋 {subSummary}</span>}
                          {t.comment_count > 0 && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                              <MessageSquare size={11} /> {t.comment_count}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {area.id !== 0 && onQuickAdd && (
                  <div style={{ marginTop: 6 }}>
                    <QuickAddRow areaName={area.name} onAdd={(title) => onQuickAdd(area.id, title, view === 'backlog' ? 'backlog' : 'planned')} />
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
